import { useCallback } from 'react';
import { isApolloError } from '@apollo/client';
import { addBreadcrumb } from '@sentry/react';

import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';

import { useDeviceTokenRegisterMutation } from '@ocx/graphql';
import { getDeviceId, getDevicePlatform, getDeviceType } from '@ocx/utils-device';
import { logInDev } from '@ocx/utils';

import { getCachedPushNotificationsState, cachePushNotificationsState } from './push-notification.cache';
import { PushNotificationPermissionDeniedError } from './push-notification.exception';
import { getFCMToken, getIsChanged, getIsPushNotificationRegistrationExpired } from './push-notifications.utils';

const getFreshDeviceIdAndTokenOrNull = async () => {
  const devicePlatform = getDevicePlatform();
  // Push notifications are not supported on the web.
  if (devicePlatform === 'web') {
    return null;
  }
  const cachedState = await getCachedPushNotificationsState();
  const isStateExpired = getIsPushNotificationRegistrationExpired(cachedState.registrationTimestamp);

  const { token, removeListeners } = await getFCMToken();
  const deviceId = await getDeviceId();

  const isTokenChanged = getIsChanged(
    { deviceId, token },
    { deviceId: cachedState.deviceId, token: cachedState.fcmToken },
  );

  await removeListeners();

  // Do not register token if device ID and token are not changed and not expired
  if (!isStateExpired && !isTokenChanged) {
    return null;
  }

  return {
    token,
    deviceId,
    deviceType: getDeviceType(),
  };
};

export const useRegisterPushNotifications = () => {
  const [registerDeviceToken] = useDeviceTokenRegisterMutation();
  const { triggerEvent } = useRudderStack();

  const registerPushNotifications = useCallback(async (): Promise<void> => {
    try {
      const input = await getFreshDeviceIdAndTokenOrNull();
      if (!input) {
        return;
      }
      await registerDeviceToken({
        variables: {
          input,
        },
      });
      await cachePushNotificationsState({ deviceId: input.deviceId, fcmToken: input.token });
    } catch (e) {
      logInDev(e);
      if (e instanceof PushNotificationPermissionDeniedError) {
        return;
      }
      if (e instanceof Error && !isApolloError(e)) {
        addBreadcrumb({ message: 'Register push notification failed', data: { error: e } });
        triggerEvent('push_notification_register_failed');
      }
    }
  }, [registerDeviceToken, triggerEvent]);

  return { registerPushNotifications };
};
